import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SOA_STATUSES,
  SOA_STATUS_LABELS,
  type Assessment,
  type EvidenceFile,
  type OrgMember,
  type ResponseSummaryItem,
  type Soa,
  type SoaControlRow,
} from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useOrg, type CurrentOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { SOA_COLUMN_COUNT, SoaRow, type MemberOption } from './SoaRow.js';
import { SoaVersionsPanel } from './SoaVersionsPanel.js';
import {
  ChangesDialog,
  EditHeaderDialog,
  RecordVersionDialog,
  type ChangesTarget,
} from './SoaDialogs.js';
import {
  buildLinkSummaries,
  bulkFieldErrors,
  errorMessage,
  fetchAllPages,
  fieldErrors,
  issuesOf,
  mergeEdit,
  pruneEdits,
  rowMatches,
  withSavedRows,
  without,
  type SoaEdit,
  type SoaEdits,
} from './soaModel.js';
import './soa.css';

const EMPTY_ROWS: SoaControlRow[] = [];
/** BulkUpdateSoaInput accepts at most 200 rows per request. */
const BULK_LIMIT = 200;

interface BulkResult {
  updated: number;
  items: SoaControlRow[];
}

type EditEntries = Array<[string, SoaEdit]>;

interface BulkOutcome {
  saved: SoaControlRow[];
  /** The chunk that failed (nothing in it was written) and why. */
  failure: { error: unknown; chunk: EditEntries } | null;
}

interface SectionGroup {
  number: string;
  title: string;
  rows: SoaControlRow[];
}

type ConfirmState = { kind: 'reset'; clauseId: string } | { kind: 'discard' } | null;

/**
 * Statement of Applicability (/standards/versions/:versionId/soa?assessmentId=).
 * The body is keyed by organisation and version: the org switcher keeps this
 * route mounted, and clause ids are platform-level, so without the key the
 * unsaved edits, expanded rows and row errors of one organisation would carry
 * over to another's SOA and could be saved into it.
 */
export function SoaPage() {
  const { versionId = '' } = useParams();
  const { org } = useOrg();
  return <SoaPageBody key={`${org.orgId}:${versionId}`} versionId={versionId} org={org} />;
}

/**
 * One query for the SOA, a local map of unsaved edits, memoised rows; per-row
 * PUT, "Save all changed" / "Mark section applicable" through the bulk POST.
 */
function SoaPageBody({ versionId, org }: { versionId: string; org: CurrentOrg }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  const isAssessor = org.role === 'assessor';
  const isAdmin = org.role === 'admin';
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  const base = `/orgs/${org.orgId}/soa/${versionId}`;
  const soaKey = useMemo(() => ['soa', org.orgId, versionId], [org.orgId, versionId]);

  // ------------------------------------------------------------- queries
  const soa = useQuery({
    queryKey: soaKey,
    queryFn: () => apiFetch<Soa>(base),
    enabled: !isAssessor && versionId !== '',
  });
  const assessments = useQuery({
    queryKey: ['assessments', org.orgId],
    queryFn: () => apiFetch<Assessment[]>(`/orgs/${org.orgId}/assessments`),
    enabled: !isAssessor,
  });
  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: !isAssessor,
  });
  const memberOptions = useMemo<MemberOption[]>(
    () =>
      (members.data ?? [])
        .map((member) => ({ id: member.user.id, name: member.user.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members.data],
  );

  // Assessment selector: the query param when it names an assessment of this
  // version, else the newest non-archived one (the export's own default).
  const versionAssessments = useMemo(
    () => (assessments.data ?? []).filter((a) => a.standardVersionId === versionId),
    [assessments.data, versionId],
  );
  const requestedAssessmentId = params.get('assessmentId') ?? '';
  const assessmentId = useMemo(() => {
    if (requestedAssessmentId && versionAssessments.some((a) => a.id === requestedAssessmentId)) {
      return requestedAssessmentId;
    }
    return versionAssessments.find((a) => a.status !== 'archived')?.id ?? '';
  }, [requestedAssessmentId, versionAssessments]);
  const selectAssessment = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('assessmentId', id);
    else next.delete('assessmentId');
    setParams(next, { replace: true });
  };

  // The read-only Evidence / Response status column, derived like the export.
  const responses = useQuery({
    queryKey: ['soa-responses', assessmentId],
    queryFn: () =>
      fetchAllPages<ResponseSummaryItem>(
        `/assessments/${assessmentId}/responses`,
        { fields: 'summary' },
        500,
        4,
      ),
    enabled: assessmentId !== '',
  });
  const evidence = useQuery({
    queryKey: ['soa-evidence', org.orgId, assessmentId],
    queryFn: () =>
      fetchAllPages<EvidenceFile>(`/orgs/${org.orgId}/evidence`, { assessmentId }, 200, 10),
    enabled: assessmentId !== '',
  });
  const links = useMemo(
    () => buildLinkSummaries(responses.data ?? [], evidence.data ?? []),
    [responses.data, evidence.data],
  );
  const linksReady = assessmentId !== '' && !responses.isPending && !evidence.isPending;

  // --------------------------------------------------------- local state
  const rows = soa.data?.controls ?? EMPTY_ROWS;
  const rowById = useMemo(() => new Map(rows.map((row) => [row.clauseId, row])), [rows]);
  const rowByIdRef = useRef(rowById);
  rowByIdRef.current = rowById;

  const [edits, setEdits] = useState<SoaEdits>({});
  const editsRef = useRef(edits);
  editsRef.current = edits;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, Record<string, string>>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const [sectionFilter, setSectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [applicableFilter, setApplicableFilter] = useState('');
  const [search, setSearch] = useState('');

  const [versionsOpen, setVersionsOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [headerOpen, setHeaderOpen] = useState(false);
  const [changesFor, setChangesFor] = useState<ChangesTarget | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  // Server rows changed (a refetch after a save): drop edits that no longer differ.
  useEffect(() => {
    setEdits((prev) => pruneEdits(prev, rowById));
  }, [rowById]);

  const dirtyIds = useMemo(() => Object.keys(edits), [edits]);
  const dirtyCount = dirtyIds.length;

  // Unsaved edits live only in this page: warn before the tab is closed.
  useEffect(() => {
    if (dirtyCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older engines only honour the legacy returnValue.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyCount]);

  // ------------------------------------------------- stable row callbacks
  const onChange = useCallback((clauseId: string, patch: SoaEdit) => {
    setEdits((prev) => {
      const row = rowByIdRef.current.get(clauseId);
      if (!row) return prev;
      const merged = mergeEdit(row, prev[clauseId], patch);
      const next = { ...prev };
      if (merged) next[clauseId] = merged;
      else delete next[clauseId];
      return next;
    });
    // The inline issues of the fields being edited are stale now.
    setRowErrors((prev) => {
      const current = prev[clauseId];
      if (!current) return prev;
      const remaining = { ...current };
      for (const key of Object.keys(patch)) delete remaining[key];
      if (Object.keys(remaining).length === Object.keys(current).length) return prev;
      return Object.keys(remaining).length > 0
        ? { ...prev, [clauseId]: remaining }
        : without(prev, clauseId);
    });
  }, []);

  const onToggle = useCallback((clauseId: string, open?: boolean) => {
    setExpanded((prev) => {
      const wanted = open ?? !prev.has(clauseId);
      if (wanted === prev.has(clauseId)) return prev;
      const next = new Set(prev);
      if (wanted) next.add(clauseId);
      else next.delete(clauseId);
      return next;
    });
  }, []);

  const onDiscard = useCallback((clauseId: string) => {
    setEdits((prev) => without(prev, clauseId));
    setRowErrors((prev) => without(prev, clauseId));
  }, []);

  const onResetRequest = useCallback((clauseId: string) => {
    setConfirm({ kind: 'reset', clauseId });
  }, []);

  // ----------------------------------------------------------- mutations
  const applySavedRows = (saved: ReadonlyArray<SoaControlRow>) => {
    queryClient.setQueryData<Soa>(soaKey, (old) => (old ? withSavedRows(old, saved) : old));
    void queryClient.invalidateQueries({ queryKey: soaKey });
  };

  const saveRow = useMutation({
    mutationFn: ({ clauseId, body }: { clauseId: string; body: SoaEdit }) =>
      apiFetch<SoaControlRow>(`${base}/controls/${clauseId}`, { method: 'PUT', body }),
    onMutate: ({ clauseId }) => setSavingId(clauseId),
    onSettled: () => setSavingId(null),
    onSuccess: (saved, { clauseId }) => {
      setEdits((prev) => without(prev, clauseId));
      setRowErrors((prev) => without(prev, clauseId));
      applySavedRows([saved]);
      toast(`${saved.clauseNumber} saved.`, 'success');
    },
    onError: (err, { clauseId }) => {
      const issues = issuesOf(err);
      if (issues.length > 0) {
        setRowErrors((prev) => ({ ...prev, [clauseId]: fieldErrors(issues) }));
        onToggle(clauseId, true);
        toast('The control could not be saved — see the highlighted field.', 'error');
        return;
      }
      toast(errorMessage(err, 'Could not save the control'), 'error');
    },
  });
  const { mutate: mutateRow } = saveRow;
  const onSave = useCallback(
    (clauseId: string) => {
      const body = editsRef.current[clauseId];
      if (body) mutateRow({ clauseId, body });
    },
    [mutateRow],
  );

  const bulkSave = async (entries: EditEntries): Promise<BulkOutcome> => {
    const saved: SoaControlRow[] = [];
    for (let start = 0; start < entries.length; start += BULK_LIMIT) {
      const chunk = entries.slice(start, start + BULK_LIMIT);
      try {
        const result = await apiFetch<BulkResult>(`${base}/controls`, {
          method: 'POST',
          body: { controls: chunk.map(([clauseId, edit]) => ({ clauseId, ...edit })) },
        });
        saved.push(...result.items);
      } catch (error) {
        return { saved, failure: { error, chunk } };
      }
    }
    return { saved, failure: null };
  };

  const saveAll = useMutation({
    mutationFn: bulkSave,
    onSuccess: ({ saved, failure }) => {
      if (saved.length > 0) {
        const savedIds = new Set(saved.map((row) => row.clauseId));
        setEdits((prev) => {
          const next: SoaEdits = {};
          for (const [clauseId, edit] of Object.entries(prev)) {
            if (!savedIds.has(clauseId)) next[clauseId] = edit;
          }
          return next;
        });
        setRowErrors((prev) => {
          const next = { ...prev };
          for (const clauseId of savedIds) delete next[clauseId];
          return next;
        });
        applySavedRows(saved);
      }
      if (!failure) {
        toast(`Saved ${saved.length} control${saved.length === 1 ? '' : 's'}.`, 'success');
        return;
      }
      const issues = issuesOf(failure.error);
      const perRow = bulkFieldErrors(issues, (index) => failure.chunk[index]?.[0]);
      const failedIds = Object.keys(perRow);
      if (failedIds.length > 0) {
        setRowErrors((prev) => ({ ...prev, ...perRow }));
        setExpanded((prev) => new Set([...prev, ...failedIds]));
        toast(
          `${failedIds.length} control${failedIds.length === 1 ? '' : 's'} could not be saved — see the highlighted fields. Nothing else in that batch was written.`,
          'error',
        );
        return;
      }
      toast(errorMessage(failure.error, 'Could not save the changed controls'), 'error');
    },
    onError: (err) => toast(errorMessage(err, 'Could not save the changed controls'), 'error'),
  });

  const markSection = useMutation({
    mutationFn: (sectionNumber: string) =>
      apiFetch<BulkResult>(`${base}/controls`, {
        method: 'POST',
        body: {
          controls: rows
            .filter((row) => row.sectionNumber === sectionNumber && !row.applicable)
            .map((row) => ({ clauseId: row.clauseId, applicable: true })),
        },
      }),
    onSuccess: (result) => {
      applySavedRows(result.items);
      toast(
        `${result.updated} control${result.updated === 1 ? '' : 's'} marked applicable.`,
        'success',
      );
    },
    onError: (err) => toast(errorMessage(err, 'Could not update the section'), 'error'),
  });

  const seed = useMutation({
    mutationFn: () => apiFetch<{ filled: number }>(`${base}/seed`, { method: 'POST' }),
    onSuccess: ({ filled }) => {
      void queryClient.invalidateQueries({ queryKey: soaKey });
      toast(
        filled > 0
          ? `Best-practice text saved to ${filled} empty row${filled === 1 ? '' : 's'}.`
          : 'Every row already has text — nothing was filled.',
        'success',
      );
    },
    onError: (err) => toast(errorMessage(err, 'Could not fill the empty rows'), 'error'),
  });

  const applyReset = (clauseId: string) => {
    const row = rowById.get(clauseId);
    if (!row?.seed) return;
    onChange(clauseId, {
      reasons: row.seed.reasons.map((entry) => ({ reason: entry.reason, detail: entry.detail })),
      justification: row.seed.justification,
      implementation: row.seed.implementation,
      evidenceNote: row.seed.evidenceNote,
    });
    onToggle(clauseId, true);
  };

  const discardAll = () => {
    setEdits({});
    setRowErrors({});
  };

  // ------------------------------------------------------------- filters
  const sectionOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of rows) if (!seen.has(row.sectionNumber)) seen.set(row.sectionNumber, row.sectionTitle);
    return [...seen.entries()];
  }, [rows]);

  // Filters read the server values, so a row being edited stays in view until saved.
  const sections = useMemo<SectionGroup[]>(() => {
    const needle = search.trim().toLowerCase();
    const groups: SectionGroup[] = [];
    let current: SectionGroup | null = null;
    for (const row of rows) {
      if (sectionFilter && row.sectionNumber !== sectionFilter) continue;
      if (statusFilter && row.implementationStatus !== statusFilter) continue;
      if (applicableFilter === 'yes' && !row.applicable) continue;
      if (applicableFilter === 'no' && row.applicable) continue;
      if (needle && !rowMatches(row, needle)) continue;
      if (!current || current.number !== row.sectionNumber) {
        current = { number: row.sectionNumber, title: row.sectionTitle, rows: [] };
        groups.push(current);
      }
      current.rows.push(row);
    }
    return groups;
  }, [rows, sectionFilter, statusFilter, applicableFilter, search]);

  const excludedBySection = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (!row.applicable) counts.set(row.sectionNumber, (counts.get(row.sectionNumber) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  // -------------------------------------------------------------- render
  if (isAssessor) {
    return (
      <EmptyState
        title="Members only"
        message="The Statement of Applicability is maintained by the organisation's own members. Assessors work from the assessment and its evidence."
      />
    );
  }
  if (soa.isPending) return <LoadingPane label="Loading the Statement of Applicability" />;
  if (soa.isError) {
    const apiError = soa.error instanceof ApiRequestError ? soa.error : null;
    const notFound = apiError?.status === 404;
    // A standard whose Statement of Applicability switch is off 404s every SOA
    // route with its own `details.reason`, so a deep link or a bookmark
    // explains itself rather than looking broken; an unknown standard version
    // keeps the other wording. Match the tag, not the message, so a copy edit
    // on the server cannot silently turn this back into the generic 404.
    const notSupported = notFound && apiError?.details?.reason === 'soa_not_supported';
    if (notSupported) {
      return (
        <div>
          <PageHeader title="Statement of Applicability" />
          <EmptyState
            title="Not used by this standard"
            message="A Statement of Applicability declares which controls apply to the organisation and why, so it is offered for standards with a control set to declare applicability over, such as ISO 27001. A platform administrator can switch it on for this standard under Manage standards."
            action={
              <Link to={`/standards/versions/${versionId}`}>
                <Button variant="secondary">Back to the standard</Button>
              </Link>
            }
          />
        </div>
      );
    }
    return (
      <div>
        <PageHeader title="Statement of Applicability" />
        <EmptyState
          title="Could not load the Statement of Applicability"
          message={
            notFound
              ? 'This standard version was not found.'
              : 'Something went wrong — please try again shortly.'
          }
          action={
            !notFound && (
              <Button variant="secondary" onClick={() => void soa.refetch()}>
                Retry
              </Button>
            )
          }
        />
      </div>
    );
  }

  const data = soa.data;
  const summary = data.summary;
  const latest = data.latestVersion;
  const hasSeed = rows.some((row) => row.seed !== null);
  const exportHref = apiUrl(
    `${base}/export.xlsx${assessmentId ? `?assessmentId=${encodeURIComponent(assessmentId)}` : ''}`,
  );
  const confirmRow = confirm?.kind === 'reset' ? rowById.get(confirm.clauseId) : undefined;

  return (
    <div className="soa-page">
      <p>
        <Link to={`/standards/versions/${versionId}`}>
          ← {data.standardName} {data.versionLabel}
        </Link>
      </p>
      <PageHeader
        title={`Statement of Applicability — ${data.standardName} ${data.versionLabel}`}
        subtitle={`${summary.total} controls · ${summary.applicable} applicable · ${summary.implemented} implemented · ${summary.partiallyImplemented} partially · ${summary.planned} planned${
          summary.notApplicable > 0 ? ` · ${summary.notApplicable} not applicable` : ''
        }`}
        actions={
          <>
            {canWrite && hasSeed && (
              <Button
                variant="secondary"
                loading={seed.isPending}
                onClick={() => seed.mutate()}
                title="Saves the best-practice text into rows with no justification or implementation text; rows with your own wording are never changed"
              >
                Fill empty rows
              </Button>
            )}
            {isAdmin && (
              <Button variant="secondary" onClick={() => setHeaderOpen(true)}>
                Edit header
              </Button>
            )}
            <Button
              variant="secondary"
              aria-pressed={versionsOpen}
              onClick={() => setVersionsOpen((open) => !open)}
            >
              Versions
            </Button>
            <a href={exportHref}>
              <Button variant="secondary">Download Excel</Button>
            </a>
            {isAdmin && <Button onClick={() => setRecordOpen(true)}>Record version</Button>}
          </>
        }
      />

      <Card className="soa-header-card">
        <div className="soa-header-meta">
          <Badge tone="neutral">{data.classification}</Badge>
          <span className="soa-muted">
            {latest
              ? `Latest version ${latest.label} · recorded ${formatDate(latest.createdAt)} by ${latest.authorName}${
                  latest.approvedByName
                    ? ` · approved by ${latest.approvedByName}${
                        latest.approvedOn ? ` on ${formatDate(latest.approvedOn)}` : ''
                      }`
                    : ' · not yet approved'
                }`
              : 'No version recorded yet — the export is marked Draft'}
          </span>
        </div>
        <p className="soa-purpose">{data.purpose}</p>
      </Card>

      <div className={`soa-layout${versionsOpen ? ' with-panel' : ''}`}>
        <div className="soa-main">
          <div className="soa-toolbar">
            <div className="soa-search">
              <Field label="Search">
                {(props) => (
                  <Input
                    {...props}
                    type="search"
                    placeholder="Number, title, wording, owner…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <Field label="Section">
              {(props) => (
                <Select {...props} value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
                  <option value="">All sections</option>
                  {sectionOptions.map(([number, title]) => (
                    <option key={number} value={number}>
                      {number} {title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Status">
              {(props) => (
                <Select {...props} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All statuses</option>
                  {SOA_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {SOA_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Applicable">
              {(props) => (
                <Select
                  {...props}
                  value={applicableFilter}
                  onChange={(e) => setApplicableFilter(e.target.value)}
                >
                  <option value="">All controls</option>
                  <option value="yes">Applicable</option>
                  <option value="no">Not applicable</option>
                </Select>
              )}
            </Field>
            <div className="soa-assessment">
              <Field
                label="Assessment"
                hint="Feeds the evidence column and the Excel export"
              >
                {(props) =>
                  versionAssessments.length === 0 ? (
                    <Select {...props} value="" disabled>
                      <option value="">
                        {assessments.isPending ? 'Loading…' : 'No assessments for this version'}
                      </option>
                    </Select>
                  ) : (
                    <Select
                      {...props}
                      value={assessmentId}
                      onChange={(e) => selectAssessment(e.target.value)}
                    >
                      {!assessmentId && <option value="">No assessment</option>}
                      {versionAssessments.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                          {a.status !== 'open' ? ` (${a.status})` : ''}
                        </option>
                      ))}
                    </Select>
                  )
                }
              </Field>
            </div>
          </div>

          <Table
            head={
              <>
                <th>Control</th>
                <th>Applicable</th>
                <th>Reasons</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Evidence / responses</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </>
            }
          >
            {sections.map((section) => {
              const excluded = excludedBySection.get(section.number) ?? 0;
              return (
                <Fragment key={section.number}>
                  <tr className="soa-section-row">
                    <th colSpan={SOA_COLUMN_COUNT}>
                      <div className="soa-section-head">
                        <span>
                          {section.number} {section.title} · {section.rows.length} control
                          {section.rows.length === 1 ? '' : 's'}
                        </span>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={excluded === 0}
                            loading={markSection.isPending && markSection.variables === section.number}
                            title={
                              excluded === 0
                                ? 'Every control in this section is already applicable'
                                : `Mark the ${excluded} excluded control${excluded === 1 ? '' : 's'} applicable and save`
                            }
                            onClick={() => markSection.mutate(section.number)}
                          >
                            Mark section applicable{excluded > 0 ? ` (${excluded})` : ''}
                          </Button>
                        )}
                      </div>
                    </th>
                  </tr>
                  {section.rows.map((row) => (
                    <SoaRow
                      key={row.clauseId}
                      row={row}
                      edit={edits[row.clauseId]}
                      expanded={expanded.has(row.clauseId)}
                      canWrite={canWrite}
                      saving={savingId === row.clauseId}
                      errors={rowErrors[row.clauseId]}
                      link={links.get(row.clauseId)}
                      hasAssessment={assessmentId !== ''}
                      linksReady={linksReady}
                      members={memberOptions}
                      onChange={onChange}
                      onToggle={onToggle}
                      onSave={onSave}
                      onDiscard={onDiscard}
                      onResetRequest={onResetRequest}
                    />
                  ))}
                </Fragment>
              );
            })}
          </Table>
          {sections.length === 0 && (
            <p className="soa-muted soa-empty">
              {rows.length === 0
                ? 'This standard version has no controls.'
                : 'No controls match the current filters.'}
            </p>
          )}

          {canWrite && dirtyCount > 0 && (
            <div className="soa-save-bar" role="region" aria-label="Unsaved changes">
              <span>
                {dirtyCount} control{dirtyCount === 1 ? '' : 's'} with unsaved changes
              </span>
              <div className="soa-save-bar-actions">
                <Button variant="secondary" onClick={() => setConfirm({ kind: 'discard' })}>
                  Discard all
                </Button>
                <Button loading={saveAll.isPending} onClick={() => saveAll.mutate(Object.entries(edits))}>
                  Save all changed
                </Button>
              </div>
            </div>
          )}
        </div>

        {versionsOpen && (
          <SoaVersionsPanel
            orgId={org.orgId}
            versionId={versionId}
            latest={latest}
            onViewChanges={setChangesFor}
            onClose={() => setVersionsOpen(false)}
          />
        )}
      </div>

      <RecordVersionDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        orgId={org.orgId}
        versionId={versionId}
        members={memberOptions}
        latest={latest}
      />
      <EditHeaderDialog
        open={headerOpen}
        onClose={() => setHeaderOpen(false)}
        orgId={org.orgId}
        versionId={versionId}
        purpose={data.purpose}
        classification={data.classification}
      />
      <ChangesDialog
        orgId={org.orgId}
        versionId={versionId}
        target={changesFor}
        onClose={() => setChangesFor(null)}
      />

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === 'discard' ? 'Discard all unsaved changes?' : 'Reset row to best practice?'}
      >
        {confirm?.kind === 'discard' ? (
          <p>
            The unsaved changes on {dirtyCount} control{dirtyCount === 1 ? '' : 's'} will be thrown
            away and the rows will show what is saved.
          </p>
        ) : (
          <p>
            {confirmRow
              ? `The reasons, justification, implementation details and evidence note of ${confirmRow.clauseNumber} ${confirmRow.title} are replaced with the best-practice text. Nothing is saved until you press Save on the row.`
              : ''}
          </p>
        )}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          {confirm?.kind === 'discard' ? (
            <Button
              variant="danger"
              onClick={() => {
                discardAll();
                setConfirm(null);
              }}
            >
              Discard all
            </Button>
          ) : (
            <Button
              onClick={() => {
                if (confirm?.kind === 'reset') applyReset(confirm.clauseId);
                setConfirm(null);
              }}
            >
              Reset row
            </Button>
          )}
        </div>
      </Dialog>
    </div>
  );
}
